import React, { useState, useEffect } from 'react';
import { scrollToSection } from '../utils/scroll';

const images = [
  {
    src: '/1.jpg',
    alt: 'Luxury swimming pool in Cyprus',
    lowResSrc: '/1-low.jpg' // Optional low-res version for preloading
  },
  {
    src: '/2.jpg',
    alt: 'Professional pool maintenance service',
    lowResSrc: '/2-low.jpg'
  },
  {
    src: '/3.jpg',
    alt: 'Modern pool equipment installation',
    lowResSrc: '/3-low.jpg'
  },
  {
    src: '/4.jpg',
    alt: 'Crystal clear pool water treatment',
    lowResSrc: '/4-low.jpg'
  }
];

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Preload images
  useEffect(() => {
    const preloadImages = () => {
      images.forEach((image, index) => {
        const img = new Image();
        img.src = image.src;
        img.onload = () => {
          setLoadedImages(prev => new Set([...prev, index]));
        };
      });
    };
    preloadImages();
  }, []);

  // Handle slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      
      // Reset transition state after animation
      setTimeout(() => {
        setIsTransitioning(false);
      }, 1000);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    scrollToSection(sectionId);
  };

  return (
    <div className="relative h-[80vh] overflow-hidden">
      {/* Slideshow */}
      {images.map((image, index) => {
        const isLoaded = loadedImages.has(index);
        const isActive = index === currentImageIndex;
        
        return (
          <div
            key={image.src}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out pointer-events-none ${
              isActive ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ willChange: 'opacity' }}
          >
            {/* Low-res background image (optional) */}
            {image.lowResSrc && !isLoaded && (
              <img
                src={image.lowResSrc}
                alt=""
                className="absolute inset-0 w-full h-full object-cover filter blur-sm scale-105"
                loading="lazy"
              />
            )}
            
            {/* High-res main image */}
            <img
              src={image.src}
              alt={image.alt}
              className={`absolute inset-0 w-full h-full object-cover transform scale-[1.01] ${
                isTransitioning ? 'transition-transform duration-10000 scale-105' : ''
              }`}
              style={{
                objectPosition: 'center center',
                imageRendering: 'crisp-edges',
                willChange: 'transform'
              }}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </div>
        );
      })}

      {/* Overlay with reduced opacity for better image visibility */}
      <div className="absolute inset-0 bg-cyan-900/60 backdrop-blur-[1px] pointer-events-none" aria-hidden="true" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex items-center pointer-events-auto">
        <div className="max-w-3xl">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 lg:mb-8 leading-tight animate-fadeIn">
            Your Pool Deserves <br />
            <span className="text-cyan-300">Expert Installation & Care</span>
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 mb-8 lg:mb-12 leading-relaxed animate-slideDown max-w-2xl">
          Professional pool system services in Cyprus from technical installations and filtration systems
          to long term maintenance. Trusted with over 25 years of experience..
          </p>
          <div className="space-x-4 lg:space-x-6 animate-scaleIn">
            <a
              href="#contact"
              onClick={(e) => handleClick(e, 'contact')}
              className="bg-cyan-500 hover:bg-cyan-400 text-white px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold transition-all duration-300 hover:transform hover:scale-105 inline-flex items-center gap-2 shadow-lg hover:shadow-cyan-500/50"
            >
              Get Free Consultation
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
            </a>
            <a
              href="#services"
              onClick={(e) => handleClick(e, 'services')}
              className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold transition-all duration-300 hover:transform hover:scale-105 inline-flex items-center gap-2 border border-white/20"
            >
              Explore Services
            </a>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setIsTransitioning(true);
              setCurrentImageIndex(index);
              setTimeout(() => setIsTransitioning(false), 1000);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentImageIndex
                ? 'bg-white w-6'
                : 'bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Hero;